/**
 * Training Data API Service
 *
 * API integration layer for Q.AI RLHF Training Dashboard
 * Endpoints: /api/admin/qai/training/* (FIX Engine proxies to DataFeed)
 *
 * Tasks: F.5 - API Integration Layer
 * Reference: HANDOFF_RLHF_TRAINING_WORKFLOW.md
 */

import axios from 'axios';
import { getQAIAdminConfig } from '../config';

// Get routing configuration (DataFeed direct or FIX Engine proxy)
const { baseUrl, pathPrefix } = getQAIAdminConfig();

/**
 * Date preset calculations
 * Returns { from: Date, to: Date } for each preset
 */
const getDateRangeFromPreset = (preset, customFrom, customTo) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfToday = new Date(today);
  endOfToday.setHours(23, 59, 59, 999);

  switch (preset) {
    case 'today':
      return { from: today, to: endOfToday };

    case 'yesterday': {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const endOfYesterday = new Date(yesterday);
      endOfYesterday.setHours(23, 59, 59, 999);
      return { from: yesterday, to: endOfYesterday };
    }

    case 'last3days': {
      const threeDaysAgo = new Date(today);
      threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
      return { from: threeDaysAgo, to: endOfToday };
    }

    case 'last7days': {
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return { from: sevenDaysAgo, to: endOfToday };
    }

    case 'custom':
      return {
        from: customFrom ? new Date(customFrom) : today,
        to: customTo ? new Date(customTo) : endOfToday
      };

    default:
      return { from: today, to: endOfToday };
  }
};

/**
 * Format date for API query parameter
 */
const formatDateForApi = (date) => {
  if (!date) return null;
  return date.toISOString();
};

/**
 * Get auth token from localStorage
 */
const getAuthToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found. Please log in.');
  }
  return token;
};

/**
 * Standard headers for API requests
 */
const getHeaders = (token) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json',
  'Accept': 'application/json',
});

// ============================================================================
// TRAINING DATA API
// ============================================================================

/**
 * B.1: Export training data for Claude review
 *
 * @param {Object} params - Export parameters
 * @param {string} params.preset - Date preset: 'today', 'yesterday', 'last3days', 'last7days', 'custom'
 * @param {string} params.format - Export format: 'json' or 'csv'
 * @param {Date} params.fromDate - Custom range start (required if preset='custom')
 * @param {Date} params.toDate - Custom range end (required if preset='custom')
 * @returns {Promise<Object>} Export response with data or blob
 */
export const exportTrainingData = async (params = {}) => {
  try {
    const token = getAuthToken();
    const { preset = 'today', format = 'json', fromDate, toDate } = params;

    // Calculate date range from preset
    const dateRange = getDateRangeFromPreset(preset, fromDate, toDate);

    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('preset', preset);
    queryParams.append('format', format);

    if (dateRange.from) {
      queryParams.append('fromDate', formatDateForApi(dateRange.from));
    }
    if (dateRange.to) {
      queryParams.append('toDate', formatDateForApi(dateRange.to));
    }

    const url = `${baseUrl}${pathPrefix}/training/export?${queryParams.toString()}`;
    console.log('[trainingApi] exportTrainingData:', url);

    const response = await axios.get(url, {
      headers: getHeaders(token),
      responseType: format === 'csv' ? 'blob' : 'json'
    });

    console.log('[trainingApi] exportTrainingData response received');

    // Handle blob response (CSV)
    if (format === 'csv' && response.data instanceof Blob) {
      const filename = `qai_training_${preset}_${new Date().toISOString().split('T')[0]}.csv`;
      return {
        success: true,
        data: response.data,
        filename,
        isBlob: true
      };
    }

    // Handle JSON response - map backend structure to frontend expected structure
    const responseData = response.data?.data || response.data;
    return {
      success: true,
      exportId: responseData.exportId || `EXP-${Date.now()}`,
      dateRange: {
        from: responseData.fromDate || formatDateForApi(dateRange.from),
        to: responseData.toDate || formatDateForApi(dateRange.to)
      },
      totalConversations: responseData.count || responseData.totalConversations || 0,
      totalMessages: responseData.totalMessages || 0,
      exportedAt: responseData.exportedAt || new Date().toISOString(),
      // Map 'records' (backend) to 'data' (frontend)
      data: responseData.records || responseData.data || responseData || []
    };

  } catch (error) {
    console.error('[trainingApi] exportTrainingData error:', error);
    console.error('[trainingApi] Error response:', error.response?.data);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to export training data'
    };
  }
};

/**
 * B.2: Import bulk scores from Claude review
 *
 * @param {Object} data - Import data
 * @param {string} data.reviewedBy - Reviewer identifier (e.g., 'claude-opus-4')
 * @param {string} data.reviewDate - Review date (ISO string)
 * @param {Array} data.scores - Array of score items
 * @returns {Promise<Object>} Import result with counts
 */
export const importTrainingScores = async (data) => {
  try {
    const token = getAuthToken();

    // Validate required fields
    if (!data.scores || !Array.isArray(data.scores) || data.scores.length === 0) {
      return {
        success: false,
        error: 'No scores provided. Please include at least one score item.'
      };
    }

    // Backend uses BulkScoreImportRequest - reviewDate not needed (uses current timestamp)
    const requestBody = {
      reviewedBy: data.reviewedBy || 'admin',
      scores: data.scores
    };

    const url = `${baseUrl}${pathPrefix}/training/import`;
    console.log('[trainingApi] importTrainingScores:', url);
    console.log('[trainingApi] Import payload:', {
      reviewedBy: requestBody.reviewedBy,
      scoreCount: requestBody.scores.length
    });

    const response = await axios.post(url, requestBody, {
      headers: getHeaders(token)
    });

    console.log('[trainingApi] importTrainingScores response:', response.data);

    // Map backend BulkImportResultDto to frontend expected structure
    const responseData = response.data?.data || response.data;
    const imported = responseData.imported || responseData.updated || 0;
    const failed = responseData.failed || 0;
    return {
      success: responseData.success !== false,
      imported: imported,
      skipped: failed,
      totalReceived: responseData.totalReceived || data.scores.length,
      errors: responseData.errors || [],
      importedAt: responseData.importedAt || new Date().toISOString(),
      message: responseData.message || `Successfully imported ${imported} scores`
    };

  } catch (error) {
    console.error('[trainingApi] importTrainingScores error:', error);
    console.error('[trainingApi] Error response:', error.response?.data);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to import training scores',
      imported: 0,
      skipped: 0,
      errors: error.response?.data?.errors || []
    };
  }
};

/**
 * B.3: Get training data statistics for scoreboard
 *
 * @param {Object} params - Query parameters
 * @param {Date} params.fromDate - Start date
 * @param {Date} params.toDate - End date
 * @returns {Promise<Object>} Statistics with score distribution
 */
export const getTrainingStats = async (params = {}) => {
  try {
    const token = getAuthToken();
    const { fromDate, toDate } = params;

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (fromDate) {
      queryParams.append('fromDate', formatDateForApi(new Date(fromDate)));
    }
    if (toDate) {
      queryParams.append('toDate', formatDateForApi(new Date(toDate)));
    }

    const queryString = queryParams.toString();
    const url = `${baseUrl}${pathPrefix}/training/stats${queryString ? `?${queryString}` : ''}`;
    console.log('[trainingApi] getTrainingStats:', url);

    const response = await axios.get(url, {
      headers: getHeaders(token)
    });

    console.log('[trainingApi] getTrainingStats response:', response.data);

    // Map backend TrainingStatsDto to frontend expected structure
    const responseData = response.data?.data || response.data;

    // Backend uses numeric keys (-2 to +2), map to named keys for UI
    const backendScores = responseData.scoreDistribution || {};
    const scoreDistribution = {
      excellent: backendScores['2'] || backendScores.excellent || { count: 0, percentage: 0 },
      good: backendScores['1'] || backendScores.good || { count: 0, percentage: 0 },
      neutral: backendScores['0'] || backendScores.neutral || { count: 0, percentage: 0 },
      needsImprovement: backendScores['-1'] || backendScores.needsImprovement || { count: 0, percentage: 0 },
      bad: backendScores['-2'] || backendScores.bad || { count: 0, percentage: 0 }
    };

    return {
      success: true,
      period: {
        from: responseData.fromDate || fromDate || null,
        to: responseData.toDate || toDate || null
      },
      totals: {
        conversations: responseData.totalConversations || responseData.totals?.conversations || 0,
        messages: responseData.totalMessages || responseData.totals?.messages || 0,
        reviewed: responseData.reviewedCount || responseData.totals?.reviewed || 0,
        pending: responseData.pendingCount || responseData.totals?.pending || 0
      },
      scoreDistribution,
      byResponseType: responseData.byResponseType || {},
      topIssues: responseData.topIssues || [],
      trend: responseData.trend || []
    };

  } catch (error) {
    console.error('[trainingApi] getTrainingStats error:', error);
    console.error('[trainingApi] Error response:', error.response?.data);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch training statistics'
    };
  }
};

/**
 * B.4: Get quick training counts (total/reviewed/pending)
 *
 * @param {Object} params - Query parameters
 * @param {Date} params.fromDate - Start date (optional)
 * @param {Date} params.toDate - End date (optional)
 * @returns {Promise<Object>} Quick counts
 */
export const getTrainingCounts = async (params = {}) => {
  try {
    const token = getAuthToken();
    const { fromDate, toDate } = params;

    // Build query parameters
    const queryParams = new URLSearchParams();
    if (fromDate) {
      queryParams.append('fromDate', formatDateForApi(new Date(fromDate)));
    }
    if (toDate) {
      queryParams.append('toDate', formatDateForApi(new Date(toDate)));
    }

    const queryString = queryParams.toString();
    const url = `${baseUrl}${pathPrefix}/training/counts${queryString ? `?${queryString}` : ''}`;
    console.log('[trainingApi] getTrainingCounts:', url);

    const response = await axios.get(url, {
      headers: getHeaders(token)
    });

    console.log('[trainingApi] getTrainingCounts response:', response.data);

    const responseData = response.data?.data || response.data;
    return {
      success: true,
      total: responseData.total || 0,
      reviewed: responseData.reviewed || 0,
      pending: responseData.pending || 0,
      reviewedPercentage: responseData.reviewedPercentage || 0
    };

  } catch (error) {
    console.error('[trainingApi] getTrainingCounts error:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch training counts'
    };
  }
};

/**
 * B.5: Sync conversations to training data table
 *
 * @param {number} limit - Maximum records to sync (default 1000)
 * @returns {Promise<Object>} Sync result
 */
export const syncTrainingData = async (limit = 1000) => {
  try {
    const token = getAuthToken();
    const url = `${baseUrl}${pathPrefix}/training/sync?limit=${limit}`;
    console.log('[trainingApi] syncTrainingData:', url);

    const response = await axios.post(url, {}, {
      headers: getHeaders(token)
    });

    console.log('[trainingApi] syncTrainingData response:', response.data);

    const responseData = response.data?.data || response.data;
    return {
      success: responseData.success !== false,
      synced: responseData.synced || 0,
      skipped: responseData.skipped || 0,
      errorCount: responseData.errorCount || 0,
      errors: responseData.errors || [],
      message: responseData.message || `Synced ${responseData.synced || 0} conversations`
    };

  } catch (error) {
    console.error('[trainingApi] syncTrainingData error:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to sync training data'
    };
  }
};

/**
 * Download import template
 * Returns the expected JSON structure for bulk import
 */
export const getImportTemplate = () => {
  return {
    reviewedBy: "claude-opus-4",
    reviewDate: new Date().toISOString().split('T')[0],
    scores: [
      {
        conversationId: "conv_example123",
        messageIndex: 0,
        scoreOverall: 2,
        scoreAccuracy: 5,
        scoreCompleteness: 4,
        scorePersonalization: 5,
        scoreCodeQuality: null,
        scoreTokenEfficiency: 4,
        labels: ["excellent", "personalized"],
        responseType: "advice"
      },
      {
        conversationId: "conv_example123",
        messageIndex: 2,
        scoreOverall: -1,
        scoreAccuracy: 3,
        scoreCompleteness: 2,
        scorePersonalization: 2,
        scoreCodeQuality: null,
        scoreTokenEfficiency: 3,
        labels: ["truncated", "low_personalization"],
        responseType: "analysis"
      }
    ]
  };
};

/**
 * Validate import JSON structure
 * @param {Object} data - Parsed JSON data
 * @returns {Object} { valid: boolean, errors: string[] }
 */
export const validateImportData = (data) => {
  const errors = [];

  if (!data) {
    errors.push('No data provided');
    return { valid: false, errors };
  }

  if (!data.scores || !Array.isArray(data.scores)) {
    errors.push('Missing or invalid "scores" array');
    return { valid: false, errors };
  }

  if (data.scores.length === 0) {
    errors.push('Scores array is empty');
    return { valid: false, errors };
  }

  // Validate each score item
  data.scores.forEach((score, index) => {
    if (!score.conversationId) {
      errors.push(`Score #${index + 1}: Missing conversationId`);
    }
    if (typeof score.messageIndex !== 'number') {
      errors.push(`Score #${index + 1}: Missing or invalid messageIndex`);
    }
    if (score.scoreOverall !== undefined && (score.scoreOverall < -2 || score.scoreOverall > 2)) {
      errors.push(`Score #${index + 1}: scoreOverall must be between -2 and +2`);
    }

    // Validate 1-5 scale scores
    ['scoreAccuracy', 'scoreCompleteness', 'scorePersonalization', 'scoreCodeQuality', 'scoreTokenEfficiency']
      .forEach(field => {
        if (score[field] !== null && score[field] !== undefined) {
          if (score[field] < 1 || score[field] > 5) {
            errors.push(`Score #${index + 1}: ${field} must be between 1 and 5`);
          }
        }
      });

    if (score.labels && !Array.isArray(score.labels)) {
      errors.push(`Score #${index + 1}: labels must be an array`);
    }
  });

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Helper: Trigger file download from blob
 * @param {Blob} blob - File blob
 * @param {string} filename - Download filename
 */
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Helper: Trigger JSON file download
 * @param {Object} data - JSON data
 * @param {string} filename - Download filename
 */
export const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  downloadBlob(blob, filename);
};

// ============================================================================
// SCORE DEFINITIONS (Reference)
// ============================================================================

export const SCORE_DEFINITIONS = {
  overall: {
    2: { label: 'Excellent', color: '#22c55e', description: 'Perfect response, highly personalized' },
    1: { label: 'Good', color: '#3b82f6', description: 'Solid response, minor improvements possible' },
    0: { label: 'Neutral', color: '#6b7280', description: 'Acceptable but generic' },
    '-1': { label: 'Needs Improvement', color: '#f59e0b', description: 'Missing personalization or incomplete' },
    '-2': { label: 'Bad', color: '#ef4444', description: 'Wrong info, truncated, or harmful' }
  },
  labels: [
    { value: 'excellent', label: 'Excellent', category: 'positive' },
    { value: 'personalized', label: 'Personalized', category: 'positive' },
    { value: 'truncated', label: 'Truncated', category: 'negative' },
    { value: 'low_personalization', label: 'Low Personalization', category: 'negative' },
    { value: 'incomplete_code', label: 'Incomplete Code', category: 'negative' },
    { value: 'repetitive', label: 'Repetitive', category: 'negative' },
    { value: 'off_topic', label: 'Off Topic', category: 'negative' }
  ]
};

export default {
  exportTrainingData,
  importTrainingScores,
  getTrainingStats,
  getTrainingCounts,
  syncTrainingData,
  getImportTemplate,
  validateImportData,
  downloadBlob,
  downloadJson,
  SCORE_DEFINITIONS
};
